/**
 * Returns the index of the last element in the array where predicate is true, and -1
 * otherwise.
 * @param array The source array to search in
 * @param predicate find calls predicate once for each element of the array, in descending
 * order, until it finds one where predicate returns true. If such an element is found,
 * findLastIndex immediately returns that element index. Otherwise, findLastIndex returns -1.
 */
export function findLastIndex<T>(
  array: Array<T>,
  predicate: (value: T, index: number, obj: T[]) => boolean,
): number {
  let l = array.length;
  while (l--) {
    if (predicate(array[l], l, array)) return l;
  }
  return -1;
}

/**
 * Chunk an array into smaller arrays of a specified size.
 * @param array The source array to chunk
 * @param size The maximum size of each chunk
 */
export function chunkArray<T>(array: Array<T>, size: number): Array<Array<T>> {
  const chunked = [];
  let index = 0;
  while (index < array.length) {
    chunked.push(array.slice(index, size + index));
    index += size;
  }
  return chunked;
}

/**
 * Group elements of an array based on the given function.
 * @param array The source array to group
 * @param predicate The function that defines the group of each element.
 * @returns An object where the keys are the groups and the values are the elements in that group.
 */
export function group<T>(
  array: Array<T>,
  predicate: (value: T, index: number, obj: Array<T>) => string,
): Record<string, Array<T>> {
  const groups: Record<string, Array<T>> = {};
  array.forEach((item, index) => {
    const group = predicate(item, index, array);
    if (!groups[group]) groups[group] = [];
    groups[group].push(item);
  });
  return groups;
}

/**
 * Swap the position of two elements in an array in place.
 * @param array The source array
 * @param indexA The index of the first element
 * @param indexB The index of the second element
 * @returns
 */
export function swap<T>(array: Array<T>, indexA: number, indexB: number): void {
  const temp = array[indexA];
  array[indexA] = array[indexB];
  array[indexB] = temp;
}

/**
 * Check if arrays are equal.
 * @param arrayA The first array to compare
 * @param arrayB The second array to compare
 * @returns True if the arrays are equal, false otherwise.
 */
export function arraysEqual<T>(arrayA: Array<T>, arrayB: Array<T>): boolean {
  return (
    arrayA.length === arrayB.length &&
    arrayA.every((value, index) => value === arrayB[index])
  );
}
